import type { ReviewKind } from '../../../shared/progress';
import { BRIEF_TYPED_COMMENT_BODY, BRIEF_TYPED_COMMENT_NAME, type SceneVars } from '../../lib/tourScenes';
import Eyebrow from '../Eyebrow';
import {
  BRIEF_PURPOSE_BODY,
  BRIEF_PURPOSE_TITLE,
  BriefSections,
} from './PageChrome';

/** The key message the client selects, quotes and comments on. Short enough
 *  to render on one line inside the 700px doc column at canvas size, which is
 *  what lets the sweep hug it. */
const BRIEF_SENTENCE = 'Recovery is a plan, not a mystery.';

/**
 * comments stage — a doc column (left) and a comments sidebar (right), one
 * screen throughout: highlight a line, leave a note in the composer, watch
 * it land in the sidebar. No page swap, unlike the other two scenes.
 */
export default function CommentsStage({ vars, kind }: { vars: SceneVars; kind: ReviewKind }) {
  if (kind === 'brief') return <BriefCommentsStage vars={vars} />;
  return <ScriptCommentsStage vars={vars} />;
}

function ScriptCommentsStage({ vars }: { vars: SceneVars }) {
  const sweepOn = Boolean(vars.sweepOn);
  const pillVisible = Boolean(vars.pillVisible);
  const composerOpen = Boolean(vars.composerOpen);
  const composerName = (vars.composerName as string) ?? '';
  const composerText = (vars.composerText as string) ?? '';
  const posted = Boolean(vars.posted);
  const openCount = Number(vars.openCount ?? 0);

  return (
    <div className="absolute inset-0 bg-page">
      {/* Doc column */}
      <div className="absolute left-6 top-6 w-[700px] bg-card border border-line rounded-md shadow-flare px-5 py-5">
        <div className="grid grid-cols-[1.25fr_1fr] gap-2.5 mb-1.5">
          <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-faint px-3.5 pb-1.5 border-b border-line">
            Audio
          </span>
          <span className="font-mono text-[10px] font-semibold tracking-[0.14em] uppercase text-accent px-3.5 pb-1.5 border-b border-line">
            Visual
          </span>
        </div>
        <div className="grid grid-cols-[1.25fr_1fr] gap-2.5 my-2">
          <div className="px-4 py-3.5 rounded-sm text-[15px] leading-[1.5] text-fg bg-elevated">
            Every morning starts the same way: the first pour.
          </div>
          <div className="relative pl-8 px-4 py-3.5 rounded-sm text-[14px] leading-[1.5] text-muted bg-elevated">
            <span className="absolute top-3.5 left-3 font-mono text-[9px] font-semibold text-accent" aria-hidden>
              V
            </span>
            Steam over the machine, dawn light.
          </div>
        </div>
        <div className="grid grid-cols-[1.25fr_1fr] gap-2.5 my-2">
          <div className="relative px-4 py-3.5 rounded-sm text-[15px] leading-[1.5] text-fg bg-elevated">
            {/* Text-selection sweep: the highlight hugs the sentence itself
                (not the whole cell) and its right edge tracks the cursor's
                drag — same 700ms/easing as the cursor glide in
                tourScenes.ts — so it reads as "the cursor is selecting this
                text", not a box lighting up. */}
            <span className="relative inline-block">
              <span
                className="absolute -inset-y-0.5 left-0 rounded-[2px] bg-accent/30 transition-[width] duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                style={{ width: sweepOn ? '100%' : '0%' }}
                aria-hidden
              />
              <span className="relative">Aurora roasts in small batches every week.</span>
            </span>
          </div>
          <div className="relative pl-8 px-4 py-3.5 rounded-sm text-[14px] leading-[1.5] text-muted bg-elevated">
            <span className="absolute top-3.5 left-3 font-mono text-[9px] font-semibold text-accent" aria-hidden>
              V
            </span>
            Hands tipping green beans into the roaster.
          </div>
        </div>
      </div>

      {/* Selection pill — floats just above the highlighted (second) row,
          which spans roughly y 138-188; top 122 overlaps its top edge the
          way the real selection pill hugs the selection. */}
      <div
        className={`absolute top-[122px] left-[330px] transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          pillVisible && !composerOpen && !posted ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-fg text-page font-mono text-[11px] uppercase tracking-[0.08em] font-medium shadow-flare">
          + Comment
        </span>
      </div>

      {/* Comments sidebar */}
      <div className="absolute right-6 top-6 w-[340px] bg-card border border-line rounded-md p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <Eyebrow accent>// Comments</Eyebrow>
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-faint">{openCount} open · {posted ? 1 : 0}</span>
        </div>

        {composerOpen && (
          <div className="border border-accent/60 bg-elevated rounded-sm p-3 mb-3">
            <div className="border-l-2 border-accent pl-2.5 mb-3">
              <p className="text-[12px] text-muted italic">"Aurora roasts in small batches every week."</p>
            </div>
            <div className="w-full px-3 py-2 mb-2 bg-card border border-line rounded-sm text-fg text-[13px]">
              {composerName || <span className="text-faint">Your name</span>}
            </div>
            <div className="w-full px-3 py-2 bg-card border border-line rounded-sm text-fg text-[13px] min-h-[52px]">
              {composerText || <span className="text-faint">Leave a comment…</span>}
            </div>
            <div className="flex justify-end mt-3">
              <span className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-accent text-accent-fg font-semibold text-[11px]">
                Post comment
              </span>
            </div>
          </div>
        )}

        {posted && (
          <div className="rounded-sm border border-line bg-elevated/40 p-3">
            <div className="border-l-2 border-accent/60 pl-2.5 mb-2">
              <p className="text-[12px] text-muted italic">"Aurora roasts in small batches every week."</p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-medium text-[12px] text-fg">Dana</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-faint">· Client</span>
              <span className="font-mono text-[10px] text-faint ml-auto">now</span>
            </div>
            <p className="text-[13px] text-muted leading-[1.5] mt-0.5">
              Love this. Could we name a few of the cafes we supply?
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The same beat on an interview brief: the doc column is a run of titled
 * sections instead of AUDIO / VISUAL rows, and the line being selected is a
 * key message. `data-tour-target` marks the elements the dev harness measures
 * to derive this scene's cursor coordinates (preview-scenes.html, src/dev/previewScenes.tsx).
 */
function BriefCommentsStage({ vars }: { vars: SceneVars }) {
  const sweepOn = Boolean(vars.sweepOn);
  const pillVisible = Boolean(vars.pillVisible);
  const composerOpen = Boolean(vars.composerOpen);
  const composerName = (vars.composerName as string) ?? '';
  const composerText = (vars.composerText as string) ?? '';
  const posted = Boolean(vars.posted);
  const openCount = Number(vars.openCount ?? 0);

  return (
    <div className="absolute inset-0 bg-page">
      {/* Doc column */}
      <div className="absolute left-6 top-6 w-[700px] bg-card border border-line rounded-md shadow-flare px-5 py-5">
        <BriefSections
          sections={[
            { title: BRIEF_PURPOSE_TITLE, paragraphs: [BRIEF_PURPOSE_BODY] },
            {
              title: 'Key messages',
              bullets: [
                // Text-selection sweep, exactly as the script stage does it:
                // the highlight hugs the sentence itself and its right edge
                // tracks the cursor's drag (same 700ms + easing as the glide
                // in tourScenes.ts).
                <span className="relative inline-block" data-tour-target="sentence">
                  <span
                    className="absolute -inset-y-0.5 left-0 rounded-[2px] bg-accent/30 transition-[width] duration-700 ease-[cubic-bezier(0.4,0,0.2,1)]"
                    style={{ width: sweepOn ? '100%' : '0%' }}
                    aria-hidden
                  />
                  <span className="relative">{BRIEF_SENTENCE}</span>
                </span>,
                'Progress is measured, so you can see it even in the weeks it does not feel like it.',
                'Nobody gets pushed back into sport before they are ready.',
              ],
            },
          ]}
        />
      </div>

      {/* Selection pill, floating just above the selected key message (measured
          box: canvas x 65-302, y 232-254.5), clearing its text by the same
          ~20px the script stage's pill clears its row by. */}
      <div
        data-tour-target="pill"
        className={`absolute top-[184px] left-[268px] transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          pillVisible && !composerOpen && !posted ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-fg text-page font-mono text-[11px] uppercase tracking-[0.08em] font-medium shadow-flare">
          + Comment
        </span>
      </div>

      {/* Comments sidebar */}
      <div className="absolute right-6 top-6 w-[340px] bg-card border border-line rounded-md p-5">
        <div className="flex items-center gap-2.5 mb-4">
          <Eyebrow accent>// Comments</Eyebrow>
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-faint">{openCount} open · {posted ? 1 : 0}</span>
        </div>

        {composerOpen && (
          <div className="border border-accent/60 bg-elevated rounded-sm p-3 mb-3">
            <div className="border-l-2 border-accent pl-2.5 mb-3">
              <p className="text-[12px] text-muted italic">"{BRIEF_SENTENCE}"</p>
            </div>
            <div
              data-tour-target="composer-name"
              className="w-full px-3 py-2 mb-2 bg-card border border-line rounded-sm text-fg text-[13px]"
            >
              {composerName || <span className="text-faint">Your name</span>}
            </div>
            <div
              data-tour-target="composer-body"
              className="w-full px-3 py-2 bg-card border border-line rounded-sm text-fg text-[13px] min-h-[52px]"
            >
              {composerText || <span className="text-faint">Leave a comment…</span>}
            </div>
            <div className="flex justify-end mt-3">
              <span
                data-tour-target="post"
                className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-accent text-accent-fg font-semibold text-[11px]"
              >
                Post comment
              </span>
            </div>
          </div>
        )}

        {posted && (
          <div className="rounded-sm border border-line bg-elevated/40 p-3">
            <div className="border-l-2 border-accent/60 pl-2.5 mb-2">
              <p className="text-[12px] text-muted italic">"{BRIEF_SENTENCE}"</p>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-medium text-[12px] text-fg">{BRIEF_TYPED_COMMENT_NAME}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-faint">· Client</span>
              <span className="font-mono text-[10px] text-faint ml-auto">now</span>
            </div>
            <p className="text-[13px] text-muted leading-[1.5] mt-0.5">{BRIEF_TYPED_COMMENT_BODY}</p>
          </div>
        )}
      </div>
    </div>
  );
}
